import {
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useId,
  useState,
} from "react";

import EyeHide from "@/icons/ui/forms/eye-hide.svg";
import EyeShow from "@/icons/ui/forms/eye-show.svg";
import { cn } from "@/utils/cn";

import styles from "./style.module.scss";

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string | undefined;
  autoSelect?: boolean;
  label?: string;
  insideLeftComponent?: ReactNode;
  insideRightComponent?: ReactNode;
  labelRightComponent?: ReactNode;
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      autoSelect,
      error,
      label,
      type,
      insideLeftComponent,
      insideRightComponent,
      labelRightComponent,
      onFocus,
      onClick,
      className,
      ...inputProps
    },
    ref,
  ) => {
    const [isVisiblePassword, setIsVisiblePassword] = useState(false);
    const inputId = useId();

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      if (autoSelect) {
        event.currentTarget.select();
      }

      if (onFocus) {
        onFocus(event);
      }
    };

    return (
      <div
        className={cn(styles.wrapper, className)}
        onClick={onClick}
      >
        {label && (
          <div className={styles.labelWrapper}>
            <label
              className={styles.label}
              htmlFor={inputId}
            >
              {label}
            </label>
            {labelRightComponent}
          </div>
        )}

        <div className={styles.fieldContainer}>
          {insideLeftComponent && (
            <div className={cn(styles.icon, "left-6 right-auto")}>
              {insideLeftComponent}
            </div>
          )}

          <input
            className={cn(
              styles.fieldInput,
              insideLeftComponent && styles.fieldInputWithLeftIcon,
              insideRightComponent && styles.fieldInputWithRightIcon,
            )}
            ref={ref}
            id={inputId}
            type={isVisiblePassword ? "text" : type}
            onFocus={handleFocus}
            {...inputProps}
          />

          {type === "password" && (
            <button
              className={cn(styles.icon, "right-6 left-auto cursor-pointer")}
              type="button"
              title={"Show password"}
              onClick={() => setIsVisiblePassword(!isVisiblePassword)}
            >
              {isVisiblePassword ? (
                <EyeShow className={"icon"} />
              ) : (
                <EyeHide className={"icon"} />
              )}
            </button>
          )}

          {insideRightComponent && (
            <div className={cn(styles.icon, "right-6 left-auto")}>
              {insideRightComponent}
            </div>
          )}
        </div>

        {error && <div className={styles.fieldError}>{error}</div>}
      </div>
    );
  },
);

FormField.displayName = "FormField";

export default FormField;
